import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, TextField, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip, InputAdornment, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import LabelIcon from '@mui/icons-material/Label';

import { fetchData, deleteData, postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Design tokens (matches Company/Edit.js) ───────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

// ── Reusable compact text field ───────────────────────────────────────────────
const Field = ({ label, name, value, onChange, placeholder, required }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%' }}>
    <Typography sx={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}{required && <span style={{ color: C.danger, marginLeft: 2 }}>*</span>}
    </Typography>
    <TextField
      size="small"
      name={name}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder || label}
      required={required}
      inputProps={{ style: { fontSize: '12px', padding: '5px 8px' } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '7px',
          bgcolor: '#fafafa',
          '& fieldset': { borderColor: C.cardBorder },
          '&:hover fieldset': { borderColor: C.accentBorder },
          '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
        },
      }}
    />
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
function BalanceSheetIndex() {
  const history = useHistory();
  const { isAuthenticated, companyId, permissions, userId, token, logout } = useAuth();

  const hasCreateMasterPermission = permissions.includes('Create Master');
  const hasEditMasterPermission   = permissions.includes('Edit Master');
  const hasDeleteMasterPermission = permissions.includes('Delete Master');

  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Create modal
  const [createOpen, setCreateOpen]   = useState(false);
  const [newName, setNewName]         = useState('');
  const [creating, setCreating]       = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit modal
  const [editOpen, setEditOpen]       = useState(false);
  const [editData, setEditData]       = useState({ Id: '', Name: '' });
  const [updating, setUpdating]       = useState(false);
  const [editError, setEditError]     = useState('');

  // Delete dialog
  const [deleteId, setDeleteId]       = useState(null);
  const [deleteOpen, setDeleteOpen]   = useState(false);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, [isAuthenticated, companyId, token]);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchTableData = async () => {
    setLoading(true);
    try {
      const res = await fetchData('BalanceSheetApi', `/${companyId}`, token);
      if (res.responseCode === '1111') setData(res.response_data);
    } catch (err) {
      console.error('Error fetching BalanceSheet:', err);
      if (err.response?.status === 401) {
        logout();
        history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Create ──────────────────────────────────────────────────────────────────
  const openCreate = () => { setNewName(''); setCreateError(''); setCreateOpen(true); };
  const closeCreate = () => setCreateOpen(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { setCreateError('Name is required!'); return; }
    setCreating(true);
    try {
      await postData('BalanceSheetApi', {
        Name: newName.trim(),
        CreatedBy: userId,
        CompanyId: companyId,
        CreatedDate: new Date(),
      }, token);
      setCreateOpen(false);
      fetchTableData();
    } catch (err) {
      console.error('Error creating BalanceSheet head:', err);
      setCreateError('Failed to create. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────
  const openEdit = async (id) => {
    setEditError('');
    try {
      const res = await fetchData('BalanceSheetApi', `/CategoryDetails/${id}`, token);
      if (res.responseCode === '1111') {
        setEditData(res.response_data);
        setEditOpen(true);
      }
    } catch (err) {
      console.error('Error fetching BalanceSheet details:', err);
    }
  };
  const closeEdit = () => setEditOpen(false);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editData.Name?.trim()) { setEditError('Name is required!'); return; }
    setUpdating(true);
    try {
      await updateData('BalanceSheetApi', `${editData.Id}`, { ...editData, CompanyId: companyId }, token);
      setEditOpen(false);
      fetchTableData();
    } catch (err) {
      console.error('Error updating BalanceSheet head:', err);
      setEditError('Failed to update. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const openDelete = (id) => { setDeleteId(id); setDeleteOpen(true); };
  const closeDelete = () => { setDeleteId(null); setDeleteOpen(false); };

  const handleDelete = async () => {
    try {
      await deleteData('BalanceSheetApi', `${deleteId}`, token);
      closeDelete();
      fetchTableData();
    } catch (err) {
      console.error('Error deleting record:', err);
    }
  };

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filteredData = data.filter(item =>
    Object.values(item).some(v =>
      String(v).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // ── Shared dialog action button styles ──────────────────────────────────────
  const btnPrimary = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.accent, color: '#fff', borderRadius: '7px',
    px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.accentDark },
    '&:disabled': { bgcolor: C.accentBorder },
  };
  const btnSecondary = {
    fontSize: '11px', fontWeight: 600, textTransform: 'none',
    color: C.muted, bgcolor: C.card, border: `1px solid ${C.cardBorder}`,
    borderRadius: '7px', px: 1.4, py: 0.5,
    '&:hover': { bgcolor: C.accentDim, borderColor: C.accentBorder, color: C.text },
  };
  const btnDanger = {
    fontSize: '11px', fontWeight: 700, textTransform: 'none',
    bgcolor: C.danger, color: '#fff', borderRadius: '7px',
    px: 1.6, py: 0.5,
    '&:hover': { bgcolor: C.dangerDark },
  };

  return (
    <Box sx={{ bgcolor: C.bg, p: '6px 6px 6px' }}>

      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <AccountBalanceIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '13px', color: C.text }}>
            Balance Sheet
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 14, color: C.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px', fontSize: '12px', height: '28px',
                bgcolor: C.card,
                '& fieldset': { borderColor: C.cardBorder },
                '&:hover fieldset': { borderColor: C.accentBorder },
                '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1.5 },
              },
            }}
          />

          {/* Create button */}
          {hasCreateMasterPermission && (
            <Button
              onClick={openCreate}
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 13 }} />}
              sx={{
                fontSize: '11px', fontWeight: 700, textTransform: 'none',
                bgcolor: C.accent, color: '#fff', borderRadius: '7px',
                px: 1.4, py: 0.4, boxShadow: C.shadow,
                '&:hover': { bgcolor: C.accentDark },
              }}
            >
              Create
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{
        bgcolor: C.card, borderRadius: '10px', boxShadow: C.shadow,
        border: `1px solid ${C.cardBorder}`, overflow: 'hidden',
      }}>
        {/* Card header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          px: '12px', py: '7px', borderBottom: `1px solid ${C.cardBorder}`,
          bgcolor: `${C.accent}0d`,
        }}>
          <LabelIcon sx={{ fontSize: 15, color: C.accent }} />
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Balance Sheet Heads
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ fontSize: '10px', color: C.muted }}>
              {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: C.accent }} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Name', ''].map((h, i) => (
                    <TableCell key={i} sx={{
                      fontSize: '10px', fontWeight: 700, color: C.muted,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      bgcolor: '#f8fafa', borderBottom: `1px solid ${C.cardBorder}`,
                      py: '6px', px: '10px',
                      ...(i === 2 ? { textAlign: 'right' } : {}),
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4, color: C.muted, fontSize: '12px' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => (
                    <TableRow
                      key={item.id}
                      sx={{
                        '&:hover': { bgcolor: C.accentDim },
                        '&:last-child td': { borderBottom: 0 },
                        opacity: item.isActive === false ? 0.5 : 1,
                      }}
                    >
                      <TableCell sx={{ fontSize: '12px', color: C.muted, py: '5px', px: '10px', width: 40 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', color: C.text, py: '5px', px: '10px', fontWeight: 500 }}>
                        {item.name}
                      </TableCell>
                      <TableCell sx={{ py: '5px', px: '10px', textAlign: 'right', width: 80 }}>
                        {hasEditMasterPermission && (
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => openEdit(item.id)}
                              sx={{ color: '#7c3aed', '&:hover': { bgcolor: 'rgba(124,58,237,0.08)' } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasDeleteMasterPermission && (
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => openDelete(item.id)}
                              sx={{ color: C.danger, '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onClose={closeCreate} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '13px', fontWeight: 800, color: C.text, pb: 0.5,
          borderBottom: `1px solid ${C.cardBorder}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <AccountBalanceIcon sx={{ fontSize: 15, color: C.accent }} />
            Create Balance Sheet Head
          </Box>
          <IconButton size="small" onClick={closeCreate} sx={{ color: C.muted }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '14px !important' }}>
          {createError && (
            <Typography sx={{ fontSize: '11px', color: C.danger, mb: 1 }}>{createError}</Typography>
          )}
          <Field label="Name" name="Name" value={newName} required
            onChange={e => { setNewName(e.target.value); setCreateError(''); }}
            placeholder="Enter balance sheet head name" />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeCreate} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} sx={btnPrimary}
            startIcon={creating ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}>
            {creating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onClose={closeEdit} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '13px', fontWeight: 800, color: C.text, pb: 0.5,
          borderBottom: `1px solid ${C.cardBorder}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <EditIcon sx={{ fontSize: 15, color: '#7c3aed' }} />
            Edit Balance Sheet Head
          </Box>
          <IconButton size="small" onClick={closeEdit} sx={{ color: C.muted }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '14px !important' }}>
          {editError && (
            <Typography sx={{ fontSize: '11px', color: C.danger, mb: 1 }}>{editError}</Typography>
          )}
          <Field label="Name" name="Name" value={editData.Name} required
            onChange={e => { handleEditChange(e); setEditError(''); }}
            placeholder="Enter balance sheet head name" />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeEdit} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={updating} sx={btnPrimary}
            startIcon={updating ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 13 }} />}>
            {updating ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontSize: '13px', fontWeight: 800, color: C.text, borderBottom: `1px solid ${C.cardBorder}`, pb: 0.5 }}>
          Delete Record?
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <DialogContentText sx={{ fontSize: '12px', color: C.muted }}>
            Are you sure you want to delete this balance sheet head? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 0.8 }}>
          <Button onClick={closeDelete} sx={btnSecondary}>Cancel</Button>
          <Button onClick={handleDelete} sx={btnDanger}
            startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default BalanceSheetIndex;

